import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import styles from "./../styles.module.scss"
import FormTextInput from "../../../atoms/FormTextInput"
import { useMutation } from "@tanstack/react-query"
import { addGuestToTripRoom } from "../../../../services/firebase-functions/rooms"
import { CruisePassengerAgeGroup, ITripRoomGuest } from "@cruisen/contract"
import SelectAgeInput from "../../../atoms/SelectAgeInput"
import { guestSchema } from "./utils"
import { colors } from "@theme/colors"
import { FormPhoneInput } from "../../../atoms/FormPhoneInput"

type GuestFormValues = {
  firstName: string
  lastName: string
  ageGroup: string
  dob?: string
  phoneNumber?: string
  profileId?: string
}

export const AddGuestForm = ({
  tripId,
  roomId,
  closePopover,
  userPhoneNumber,
}: {
  tripId: string
  roomId: string
  closePopover: () => void
  userPhoneNumber: string
}) => {
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      ageGroup: "adult",
      dob: "",
      phoneNumber: "",
      profileId: "",
    },
    resolver: yupResolver(guestSchema(userPhoneNumber)),
    mode: "all",
  })

  const { mutateAsync: mutateAddGuestToRoom, isPending } = useMutation({
    mutationFn: addGuestToTripRoom,
    onSuccess: () => {
      closePopover()
    },
  })

  const submitForm = (values: GuestFormValues) => {
    const payload = {
      tripId,
      roomId,
      skipProfileCreation: true,
      guestData: {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        ageGroup: values.ageGroup as CruisePassengerAgeGroup,
        profileId: values.profileId ? Number(values.profileId) : undefined,
        email: "",
        phoneNumber: values.phoneNumber,
      } as Omit<ITripRoomGuest, "id">,
    }

    mutateAddGuestToRoom(payload)
  }

  return (
    <form
      className={styles.formWrapper}
      data-testid="add-guest-form"
      onSubmit={(e) => {
        e.preventDefault()
        handleSubmit(submitForm)()
      }}
    >
      <p className={styles.addGuestTitle}>Add guest</p>
      <FormTextInput
        control={control}
        autoFocus
        name="firstName"
        label="First name"
        testID="guest-first-name-input"
        parentBackgroundColor={colors.backgroundQuaternary}
        lightOutline={true}
      />
      <FormTextInput
        control={control}
        name="lastName"
        label="Last name"
        testID="guest-last-name-input"
        parentBackgroundColor={colors.backgroundQuaternary}
        lightOutline={true}
      />
      <SelectAgeInput
        control={control}
        name="ageGroup"
        label="Age group"
        parentBackgroundColor={colors.backgroundQuaternary}
        lightOutline={true}
        darkTheme={true}
      />
      <div>
        <FormPhoneInput
          control={control}
          name="phoneNumber"
          label="Phone number (optional)"
          parentBackgroundColor={colors.backgroundQuaternary}
          lightOutline={true}
        />
      </div>
      <p className={styles.addGuestNote}>
        By adding a phone number, I authorize Cruisen to text this guest on my behalf.
      </p>
      <button
        type="submit"
        disabled={isPending || !isValid}
        data-testid="submit-guest"
        className={styles.confirmButton}
      >
        {isPending ? "Adding guest..." : "Add"}
      </button>
    </form>
  )
}
